import React, { useCallback, useState } from 'react';
import { ChoiceElement, ChoiceOption } from '../../schema/choice.schema';
import styles from '../../assets/css/choice.module.css';
import { FormControl, MenuItem, Select } from '@mui/material';
import { FormDataValue } from '../../hooks/useFormData';
import ChevronIcon from './ChevronIcon';

interface SingleChoiceProps {
	element: ChoiceElement;
	onChange: (key: string, value: FormDataValue) => void;
	onValidationChange: (key: string, value: boolean) => void;
}

const SingleChoice: React.FC<SingleChoiceProps> = ({
	element: { label, options, preselected, required, placeholder, key },
	onChange,
	onValidationChange
}) => {
	const [selectedOption, setSelectedOption] = useState<string | undefined>(() => {
		if (preselected && preselected[0]) {
			return options.find((option) => option.value === preselected[0])?.value || '';
		}

		return '';
	});

	const [focused, setFocused] = useState<boolean>(false);
	const [isValid, setIsValid] = useState<boolean>(true);

	const validate = useCallback(
		(value: string) => {
			if (required && !value) {
				setIsValid(false);
				return false;
			}
			setIsValid(true);
			return true;
		},
		[required]
	);

	const handleFocus = useCallback(() => {
		setFocused(true);
	}, []);

	const handleBlur = useCallback(() => {
		setFocused(false);
	}, []);

	const handleChange = useCallback(
		(selectedValue: string) => {
			setSelectedOption(selectedValue);
			const isValid = validate(selectedValue);
			onValidationChange(key, isValid);

			const option = options.find((option) => option.value === selectedValue);
			if (!option) throw new Error(`Encounter unsupported choice option: ${selectedValue}`);
			const value = option.value;
			onChange(key, [value]);
		},
		[key, onChange, onValidationChange, validate]
	);

	return (
		<div className={styles['single-choice-container']}>
			<FormControl
				variant={label ? 'filled' : 'standard'}
				className={styles[label ? 'select-component-label' : 'select-component']}
				sx={style}
				fullWidth
				error={!isValid && focused}
			>
				<label className={`${styles['label-styles']} ${!isValid && focused ? styles['label-error'] : ''}`}>
					{label}
				</label>

				<Select
					id={key}
					displayEmpty
					value={selectedOption}
					className={styles['controlled-input']}
					onChange={(event) => handleChange(event.target.value as string)}
					onFocus={handleFocus}
					onBlur={handleBlur}
					sx={{ fontWeight: '300' }}
					required={required}
					label={label}
					IconComponent={ChevronIcon}
					renderValue={(selected) => {
						if (!selected) return <></>;

						const option = options.find((option) => option.value === selected);
						const label = option?.label;

						return label ? (
							label
						) : (
							<em className={styles['placeholder']} style={{ marginTop: '10px' }}>
								{placeholder}
							</em>
						);
					}}
				>
					{options.map(({ value, label, disabled, description }: ChoiceOption) => (
						<MenuItem key={value} value={value} disabled={disabled} sx={{ fontWeight: '300' }}>
							{label}
							{description && <span className={styles['option-description']}>{description}</span>}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</div>
	);
};

export default SingleChoice;

const style = {
	width: '100%',
	borderRadius: '5px',
	'& .MuiInputLabel-root': {
		color: '#707070'
	},
	'& .Mui-focused': {
		color: '#007bff',
		border: `1px solid #007bff !important`,
		borderRadius: '5px'
	},
	'& .MuiSelect-root': {
		borderBottomLeftRadius: '5px',
		borderBottomRightRadius: '5px'
	},
	'& .MuiInputBase-root': {
		'& .MuiInputBase-input:focus': {
			border: 'none !important',
			borderBottomLeftRadius: '5px',
			borderBottomRightRadius: '5px',
			borderTopLeftRadius: '5px !important',
			borderTopRightRadius: '5px !important'
		}
	},
	'&:focus-within': {
		border: 'none !important'
	}
};
